/**
 * Staff Work — Renewals OR Reviews (Phase A). Parameterised by `kind`; the two
 * are separate header tabs (different ecosystems).
 *
 * Personal view of the current user's cadence obligations of that kind
 * (SCH_event_instances → /sch/renewals-reviews/mine, filtered client-side).
 * Grouped into Overdue / Due / Upcoming / Completed buckets; a row opens the
 * RenewalReviewExecModal. Assessment obligations (CAT/RAT/PWT/Audit) have their
 * own tabs and are excluded server-side.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RenewalReviewExecModal from './RenewalReviewExecModal';
import { Search, RefreshCw } from 'lucide-react';
import Skeleton from '../../../components/common/Skeleton';
import NoDataFound from '../../../components/common/NoDataFound';
import Pagination from '../../../components/common/Pagination';
import FormControl from '../../../components/common/FormControl';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import Badge from '../../../components/common/Badge';
import { statusVariant } from '../../../utils/statusVariant';
import extractApiError from '../../../utils/apiError';
import {
    renewalsReviewsService,
    RenewalReviewItem,
    RrBucket,
    RrKind,
} from '../../../services/renewalsReviewsService';

const BUCKETS: { key: RrBucket }[] = [
    { key: 'overdue'   },
    { key: 'due'       },
    { key: 'upcoming'  },
    { key: 'completed' },
];

const WorkRenewalsReviewsBody: React.FC<{
    kind: RrKind;
    scope?: 'active' | 'archive';
    readOnly?: boolean;
}> = ({ kind, scope = 'active', readOnly = false }) => {
    const noun = kind === 'renewal' ? 'Renewals' : 'Reviews';
    const [items, setItems] = useState<RenewalReviewItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [openItem, setOpenItem] = useState<RenewalReviewItem | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchParams, setSearchParams] = useSearchParams();

    const refresh = async () => {
        setLoading(true);
        try {
            setItems(await renewalsReviewsService.mine());
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load renewals & reviews') });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    // Deep-link from calendar / inbox: /work?tab=renewals|reviews&open=<instance_id>
    useEffect(() => {
        const o = searchParams.get('open');
        if (!o || items.length === 0) return;
        const id = parseInt(o, 10);
        const hit = items.find(i => i.instance_id === id);
        if (hit) setOpenItem(hit);
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
    }, [searchParams, items, setSearchParams]);

    const filtered = useMemo(() => items.filter(i =>
        i.kind === kind &&
        (scope === 'archive' ? i.bucket === 'completed' : i.bucket !== 'completed') &&
        (!searchTerm ||
            i.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            i.type_label?.toLowerCase().includes(searchTerm.toLowerCase()))
    ), [items, kind, scope, searchTerm]);

    const grouped = useMemo(() => {
        const m: Record<RrBucket, RenewalReviewItem[]> = { overdue: [], due: [], upcoming: [], completed: [] };
        for (const i of filtered) m[i.bucket]?.push(i);
        return m;
    }, [filtered]);

    // Archive scope is a single flat list (completed only) → client-side paginate.
    const pagedArchive = useMemo(
        () => filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize],
    );

    // Reset to page 1 whenever the result set changes shape.
    useEffect(() => { setPage(1); }, [searchTerm, pageSize, scope]);

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                    {!readOnly && (
                        <button className="btn-icon" title="Refresh" onClick={refresh} aria-label="Refresh">
                            <RefreshCw size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-auto">
                    {loading && (
                        <div className="table-list">
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <tbody aria-busy="true" aria-live="polite">
                                        <Skeleton.TableRows cols={['2xs', 'sm', 'md', 'sm', { width: 'pill', align: 'right' }]} />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {!loading && filtered.length === 0 && (
                        <NoDataFound
                            title={`No ${noun}`}
                            message={scope === 'archive'
                                ? `No completed ${kind}s.`
                                : `You have no ${kind} obligations assigned to you.`}
                        />
                    )}

                    {/* Active scope → bucket-grouped tables (Overdue / Due / Upcoming) */}
                    {!loading && scope !== 'archive' && filtered.length > 0 && BUCKETS.map(b => {
                        const rows = grouped[b.key];
                        if (rows.length === 0) return null;
                        return (
                            <div key={b.key} className="rr-bucket-section" style={{ marginBottom: 18 }}>
                                <div className="table-list">
                                    <div className="table-list-scroll">
                                        <table className="table-list-table">
                                            <thead>
                                                <tr>
                                                    <th className="table-list-th table-list-cell--sno">S.No</th>
                                                    <th className="table-list-th">Type</th>
                                                    <th className="table-list-th">Obligation</th>
                                                    <th className="table-list-th">Due</th>
                                                    <th className="table-list-th table-list-cell--status table-list-th--right">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody aria-live="polite">
                                                {rows.map((r, idx) => (
                                                    <tr
                                                        key={r.instance_id}
                                                        className="table-list-row"
                                                        style={{ cursor: r.route_path ? 'pointer' : 'default' }}
                                                        onClick={() => setOpenItem(r)}
                                                    >
                                                        <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                                                        <td className="table-list-cell">{r.type_label || r.type_code || '—'}</td>
                                                        <td className="table-list-cell">
                                                            <span className="table-list-cell-title">
                                                                {r.title || '—'}
                                                            </span>
                                                        </td>
                                                        <td className="table-list-cell">
                                                            <span className="text-xs text-gray-600">{FormControl.formatDate(r.scheduled_date)}</span>
                                                        </td>
                                                        <td className="table-list-cell table-list-cell--status table-list-cell--right">
                                                            <Badge variant={statusVariant(r.status)} size="sm">{r.status || '—'}</Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Archive scope → single flat paginated table */}
                    {!loading && scope === 'archive' && filtered.length > 0 && (
                        <div className="table-list">
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            <th className="table-list-th">Type</th>
                                            <th className="table-list-th">Obligation</th>
                                            <th className="table-list-th">Due</th>
                                            <th className="table-list-th table-list-cell--status table-list-th--right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody aria-live="polite">
                                        {pagedArchive.map((r, idx) => (
                                            <tr
                                                key={r.instance_id}
                                                className="table-list-row"
                                                style={{ cursor: r.route_path ? 'pointer' : 'default' }}
                                                onClick={() => setOpenItem(r)}
                                            >
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">{r.type_label || r.type_code || '—'}</td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">
                                                        {r.title || '—'}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="text-xs text-gray-600">{FormControl.formatDate(r.scheduled_date)}</span>
                                                </td>
                                                <td className="table-list-cell table-list-cell--status table-list-cell--right">
                                                    <Badge variant={statusVariant(r.status)} size="sm">{r.status || '—'}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalRecords={filtered.length}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                recordLabel={noun.toLowerCase()}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />

            {openItem && (
                <RenewalReviewExecModal item={openItem} onClose={() => setOpenItem(null)} readOnly={readOnly} />
            )}
        </div>
    );
};

export default WorkRenewalsReviewsBody;
